/* Velara size guide — the side panel in snippets/size-guide.liquid. Vanilla custom element.
 *
 * Timing is the reference's (nove25.net, hosattributegroupextrainfosidepanel): the backdrop is
 * there at once, the panel slides in 100ms later over 0.3s, and every open lands on the first tab.
 * Closing slides the panel out first and drops the backdrop after the slide.
 *
 * One deliberate departure: the reference replays animate.css fadeInDown on the pane on every open
 * AND tab change — translate3d(0,-100%,0) of a pane 2000px+ tall, so the text drops in from far
 * above and reads as the panel scrolling down and back up. Here the pane does not animate on open
 * (the panel's own slide is the motion) and a tab change is a short fade with a 10px drop.
 *
 * Videos never load in view. A player loading where you can see it paints blank, then black, then
 * its poster — a visible blink right after the slide. So: the box shows the video's own poster
 * (Vimeo oEmbed, fetched when the page is idle), the player starts loading on intent (pointer or
 * touch on the trigger, or the phone sheet showing its pill) and stays invisible until it posts
 * `ready`. Closing PAUSES it through the embed API instead of reloading it, so a second open is
 * already warm.
 *
 * `open()` and `warm()` are public: the phone size sheet (_pdp-sticky-atc) carries its own trigger. */
if (!customElements.get('size-guide')) {
  customElements.define('size-guide', class extends HTMLElement {
    connectedCallback() {
      if (this.dialog) return; // already wired (the element was moved or re-inserted)
      this.dialog = this.querySelector('dialog');
      this.panel = this.dialog.querySelector('.velara-size-guide__panel');
      // On a phone the picker this sits in is hidden, and a modal under a display:none ancestor
      // never renders — so the dialog lives at the end of the body from here on.
      document.body.appendChild(this.dialog);
      this.querySelector('[data-size-guide-open]')?.addEventListener('click', () => this.open());
      this.dialog.addEventListener('click', (e) => {
        const t = e.target.closest('button');
        if (t && t.matches('[data-size-guide-close]')) this.close();
        else if (t && t.matches('[data-size-guide-tab]')) this.select(parseInt(t.dataset.sizeGuideTab, 10));
      });
      // A click on the dialog box itself, outside the panel, is a click on the dimmed backdrop.
      this.dialog.addEventListener('click', (e) => { if (e.target === this.dialog) this.close(); });
      // Esc: animate out like every other close instead of vanishing.
      this.dialog.addEventListener('cancel', (e) => { e.preventDefault(); this.close(); });

      const trigger = this.querySelector('[data-size-guide-open]');
      ['pointerenter', 'focus', 'touchstart'].forEach((ev) =>
        trigger?.addEventListener(ev, () => this.warm(), { once: true, passive: true }));
      window.addEventListener('message', (e) => this.onMessage(e));
      const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 1500));
      idle(() => this.posters());
    }

    /* Start loading the players (src from data-src). Each reveals on its `ready` message, or 800ms
       after load if the embed never sends one. */
    warm() {
      this.posters();
      this.dialog.querySelectorAll('iframe[data-src]').forEach((f) => {
        f.addEventListener('load', () => setTimeout(() => f.classList.add('is-ready'), 800), { once: true });
        f.src = f.dataset.src;
        f.removeAttribute('data-src');
      });
    }

    onMessage(e) {
      if (!/^https:\/\/([\w-]+\.)*(vimeo\.com|youtube(-nocookie)?\.com)$/.test(e.origin)) return;
      let data = e.data;
      try { if (typeof data === 'string') data = JSON.parse(data); } catch (err) { return; }
      const ready = data && (data.event === 'ready' || data.event === 'onReady' || data.event === 'initialDelivery');
      if (!ready) return;
      this.dialog.querySelectorAll('[data-size-guide-video]').forEach((f) => {
        if (f.contentWindow === e.source) f.classList.add('is-ready');
      });
    }

    /* The box's background is the video's own poster, so the player fades in over the same frame. */
    posters() {
      if (this.postered) return;
      this.postered = true;
      this.dialog.querySelectorAll('[data-size-guide-poster^="vimeo:"]').forEach((box) => {
        const id = box.dataset.sizeGuidePoster.split(':')[1];
        fetch(`https://vimeo.com/api/oembed.json?url=${encodeURIComponent(`https://vimeo.com/${id}`)}&width=1280`)
          .then((r) => (r.ok ? r.json() : null))
          .then((d) => { if (d && d.thumbnail_url) box.style.backgroundImage = `url("${d.thumbnail_url}")`; })
          .catch(() => {});
      });
      this.dialog.querySelectorAll('[data-size-guide-poster^="youtube:"]').forEach((box) => {
        const id = box.dataset.sizeGuidePoster.split(':')[1];
        box.style.backgroundImage = `url("https://i.ytimg.com/vi/${id}/hqdefault.jpg")`;
      });
    }

    open() {
      if (this.dialog.open) return;
      clearTimeout(this.closing);
      this.warm();
      this.dialog.showModal();
      // showModal's focus scrolls an off-screen panel into view where overflow:clip is unsupported
      this.dialog.scrollLeft = 0;
      this.panel.scrollTop = 0;
      const content = this.dialog.querySelector('.velara-size-guide__content');
      if (content) content.scrollTop = 0;
      setTimeout(() => {
        this.panel.classList.add('is-open');
        this.select(0, false);
      }, 100);
    }

    close() {
      if (!this.dialog.open) return;
      this.panel.classList.remove('is-open');
      this.closing = setTimeout(() => {
        this.dialog.close();
        this.stopVideos();
      }, 300);
    }

    select(i, animate = true) {
      this.dialog.querySelectorAll('[data-size-guide-tab]').forEach((t) => {
        const on = +t.dataset.sizeGuideTab === i;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-selected', String(on));
      });
      this.dialog.querySelectorAll('[data-size-guide-pane]').forEach((p) => {
        const on = +p.dataset.sizeGuidePane === i;
        if (!on && !p.hidden) this.stopVideos(p);
        p.hidden = !on;
        p.classList.toggle('is-active', on);
        p.classList.remove('is-entering');
        if (on && animate) {
          void p.offsetWidth; // restart the animation on a pane that was already showing
          p.classList.add('is-entering');
        }
      });
    }

    /* A hidden iframe keeps playing. Pause through the embed's own API: reloading it (src = src)
       would make the next open load the player in view again — the blink this file avoids. */
    stopVideos(scope = this.dialog) {
      scope.querySelectorAll('[data-size-guide-video]').forEach((f) => {
        if (!f.src || !f.contentWindow) return;
        const msg = f.dataset.sizeGuideVideo === 'youtube'
          ? { event: 'command', func: 'pauseVideo', args: '' }
          : { method: 'pause' };
        f.contentWindow.postMessage(JSON.stringify(msg), '*');
      });
    }
  });
}
